import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { Card, Image, Loader, Checkbox, Icon } from "semantic-ui-react";
import BCMessage from "../ui/BCMessage";
import BCQuoteIcon from "../../assets/bc-quote-icon.png";
import { getLoanInfoList } from "../../api/BrokerApi";
import { getMockLoanInfolist } from "../../api/mock/MockBrokerApi";
import BCKanban from "../ui/BCKanban";
import BCTable from "../ui/BCTable";
import { createErrorResponse, guid, handleErrorResponse, isValidPermission } from "../../Helpers";
import "../../styles/landing-page.scss";
import "../../styles/main.scss";
import BCCards from "../ui/BCCards";
import BCButton from "../ui/BCButton";
import BCMenuBar from "../ui/BCMenuBar";
import BCFilterSearch from "../BCFilterSearch";
import { BCFilterBar } from "../BCFilter";
import { useBCGlobalFilter } from "../../helpers/GlobalHelper";
import { GlobalConstants } from "../constants/GlobalConstants";
import { FeatureFlags } from "../constants/LocalServerConstants";
import { getIsWhiteLabeled } from "../../helpers/PermissionHelper";

const BPLoans = ({ 
  isGreaterThanMobile,
  isGreaterThanTablet,
  isSidebar,
  loanInfolist,
  setLoanInfolist,
  isMessage,
  setIsMessage,
  errorResponse, 
  setErrorResponse, 
  proxyUser,
  permissions,
  brokerColor,
  setCopiedApplicationId
 }) => {
  const [isFirstLoad, setIsFirstLoad] = useState(true as boolean);
  const [isLoading, setIsLoading] = useState(true);
  const [isKanbanView, setisKanbanView] = useState(localStorage.getItem("infolist") === "table" ? false : true);
  const [kanbanViews, setKanbanViews] = useState([] as any[]);
  const [isFetching, setIsFetching] = useState(false);
  const [currentSliceTo, setCurrentSliceTo] = useState(GlobalConstants.DefaultValues.defaultSliceKanban);
  const BPLoanKanbanLanes = 
  {
    Processing: {
      name: "Processing",
      index: 0
    },
    Underwriting: {
      name: "Underwriting",
      index: 1
    },
    PreApproval: {
      name: "Conditionally Approved",
      index: 2
    },
    PreparingDocuments: {
      name: "Preparing Documents",
      index: 3
    },
    Closing: {
      name: "Funding Conditions",
      index: 4
    },
    Funding: {
      name: "Documents Out:\nCleared to Close",
      index: 5
    }
  };
  const reducerInstance = useBCGlobalFilter(loanInfolist, { hiddenFilters: [], filterKeysList: ["loanProgram", "brokerage", "brokerEmail", "name"] });
  const [{ searchValue, isFilterOpen, getFilteredData, filteredData: filteredLoanData, filterBySearchValue }]: any = reducerInstance;

  const history = useHistory();
  const handleGMR = useCallback(() => {
    history.push(`/getmyrate/${guid()}`);
  }, [history]);

  const handleApplication = useCallback(() => {
    setCopiedApplicationId(undefined);
    history.push(`/application/borrower/${guid()}`);
  }, [history, setCopiedApplicationId]);

  const handleSummary = useCallback((id) => {
    if (FeatureFlags.IS_GAME_CHANGERS) {
      history.push(`/processing/tasks/${id}`);
    }
    else if (FeatureFlags.IS_BC_PRICING) {
      history.push(`/processing/pricing/${id}`);
    }
    else {
      history.push(`/processing/project/${id}`);
    }
    
  }, [history]);

  const handleError = useCallback(() => {
    history.push("/error");
  }, [history]);

  const filterInfolist = (loans) => {
    return loans.filter(x => ((x.name && ((x.name).toLowerCase().includes(searchValue.toLowerCase()))) || 
      (x.brokerage && (x.brokerage).toLowerCase().includes(searchValue.toLowerCase())) ||
      (x.brokerEmail && (x.brokerEmail).toLowerCase().includes(searchValue.toLowerCase()))));
  };

  const getFilteredOrEmptyData = (kanbanData) => {
    if (!kanbanData) {
      return [];
    }

    return getFilteredData(kanbanData.cards);
  };

  const sortFilteredLoansKanban = () => {
    const kanbanView = [
      {
        index: BPLoanKanbanLanes.Processing.index, 
        name: BPLoanKanbanLanes.Processing.name,
        title: Object.getOwnPropertyNames(BPLoanKanbanLanes)[BPLoanKanbanLanes.Processing.index],
        cards: filterBySearchValue(getFilteredOrEmptyData(kanbanViews[BPLoanKanbanLanes.Processing.index])).slice(0, currentSliceTo)
      }, 
      {
        index: BPLoanKanbanLanes.Underwriting.index, 
        name: BPLoanKanbanLanes.Underwriting.name, 
        title: Object.getOwnPropertyNames(BPLoanKanbanLanes)[BPLoanKanbanLanes.Underwriting.index], 
        cards: filterBySearchValue(getFilteredOrEmptyData(kanbanViews[BPLoanKanbanLanes.Underwriting.index])).slice(0, currentSliceTo)
      },
      {
        index: BPLoanKanbanLanes.PreApproval.index, 
        name: BPLoanKanbanLanes.PreApproval.name, 
        title: Object.getOwnPropertyNames(BPLoanKanbanLanes)[BPLoanKanbanLanes.PreApproval.index], 
        cards: filterBySearchValue(getFilteredOrEmptyData(kanbanViews[BPLoanKanbanLanes.PreApproval.index])).slice(0, currentSliceTo)
      },
      {
        index: BPLoanKanbanLanes.PreparingDocuments.index, 
        name: BPLoanKanbanLanes.PreparingDocuments.name, 
        title: Object.getOwnPropertyNames(BPLoanKanbanLanes)[BPLoanKanbanLanes.PreparingDocuments.index], 
        cards: filterBySearchValue(getFilteredOrEmptyData(kanbanViews[BPLoanKanbanLanes.PreparingDocuments.index])).slice(0, currentSliceTo)
      },
      {
        index: BPLoanKanbanLanes.Closing.index, 
        name: BPLoanKanbanLanes.Closing.name, 
        title: Object.getOwnPropertyNames(BPLoanKanbanLanes)[BPLoanKanbanLanes.Closing.index], 
        cards: filterBySearchValue(getFilteredOrEmptyData(kanbanViews[BPLoanKanbanLanes.Closing.index])).slice(0, currentSliceTo)
      }, 
      {
        index: BPLoanKanbanLanes.Funding.index, 
        name: BPLoanKanbanLanes.Funding.name, 
        title: Object.getOwnPropertyNames(BPLoanKanbanLanes)[BPLoanKanbanLanes.Funding.index], 
        cards: filterBySearchValue(getFilteredOrEmptyData(kanbanViews[BPLoanKanbanLanes.Funding.index])).slice(0, currentSliceTo)
      }
    ];

    return kanbanView;
  };

  const sortLoansKanban = (applicationData) => {
    const kanbanView = [
      {
        index: BPLoanKanbanLanes.Processing.index, 
        name: BPLoanKanbanLanes.Processing.name,
        title: Object.getOwnPropertyNames(BPLoanKanbanLanes)[BPLoanKanbanLanes.Processing.index], 
        cards: [] as any
      }, 
      {
        index: BPLoanKanbanLanes.Underwriting.index, 
        name: BPLoanKanbanLanes.Underwriting.name, 
        title: Object.getOwnPropertyNames(BPLoanKanbanLanes)[BPLoanKanbanLanes.Underwriting.index], 
        cards: [] as any
      },
      {
        index: BPLoanKanbanLanes.PreApproval.index, 
        name: BPLoanKanbanLanes.PreApproval.name, 
        title: Object.getOwnPropertyNames(BPLoanKanbanLanes)[BPLoanKanbanLanes.PreApproval.index], 
        cards: [] as any
      }, 
      {
        index: BPLoanKanbanLanes.PreparingDocuments.index, 
        name: BPLoanKanbanLanes.PreparingDocuments.name, 
        title: Object.getOwnPropertyNames(BPLoanKanbanLanes)[BPLoanKanbanLanes.PreparingDocuments.index], 
        cards: [] as any
      },
      {
        index: BPLoanKanbanLanes.Closing.index, 
        name: BPLoanKanbanLanes.Closing.name, 
        title: Object.getOwnPropertyNames(BPLoanKanbanLanes)[BPLoanKanbanLanes.Closing.index], 
        cards: [] as any
      }, 
      {
        index: BPLoanKanbanLanes.Funding.index, 
        name: BPLoanKanbanLanes.Funding.name, 
        title: Object.getOwnPropertyNames(BPLoanKanbanLanes)[BPLoanKanbanLanes.Funding.index], 
        cards: [] as any
      }
    ];

    let isKanbanNotSupportedError = false;
    applicationData.forEach((application) => {
      if (application.statusLookupName && application.name !== null) {
        const kanbanObjIndex = kanbanView.findIndex(x => x.title === application.statusLookupName);
        if (kanbanObjIndex !== -1) {
          kanbanView[kanbanObjIndex].cards.push(application);
        }
      }
      else {
        isKanbanNotSupportedError = true;
      }
    });

    if (isKanbanNotSupportedError) {
      createErrorResponse("Warning", "KanbanStatus Not Supported");
    }

    return kanbanView;
  };

  const sortedLoansKanban = useMemo(() => sortFilteredLoansKanban(), [filteredLoanData, currentSliceTo]);
  const convertToTableFormat = (loans) => {
    const result = [] as any[];
    loans.forEach((loan) => {
      if (loan.name !== null) {
        result.push({
          "Name": loan.name,
          "Loan Program": loan.loanProgram,
          "Properties or Units": loan.numberOfPropertiesOrUnits,
          "Submitted On": loan.submittedOn ? new Date(loan.submittedOn).toLocaleDateString() : null,
          "Status": loan.status ? loan.status.statusName : null,
          "Stage": loan.status ? loan.status.stageName : null,
          "Brokerage": loan.brokerage? loan.brokerage : null,
          "Broker Email": loan.brokerEmail? loan.brokerEmail : null
        });
      }
    });

    return result;
  };

  const handleInfolistViewType = () => {
    const infolistStyle = isKanbanView ? "table" : "card";
    localStorage.setItem("infolist", infolistStyle);
    setisKanbanView(!isKanbanView);
  };

  const handleScroll = () => {
    if (window.innerHeight - document.documentElement.scrollTop !== document.documentElement.clientHeight) {
      setIsFetching(true);
    }
    else {
      return;
    }
  };

  useEffect(() => {
    const getGMRInfoAysnc = async () => {
      try {
        let brokerageOrganizationId = undefined as any;
        const brokerageObjString = localStorage.getItem("brokerage");
        if (brokerageObjString && brokerageObjString !== "") {
          const brokerageObj = JSON.parse(brokerageObjString);
          if (brokerageObj.id) {
            brokerageOrganizationId = brokerageObj.id;
          }
        }

        const response = FeatureFlags.IS_MOCK ? await getMockLoanInfolist() : 
          brokerageOrganizationId ? await getLoanInfoList(proxyUser.id, brokerageOrganizationId) : 
          await getLoanInfoList(proxyUser.id);
        if (response && response.ok) {
          setIsMessage(false);
          const loanInfolistData = await response.json();
          loanInfolistData.forEach((loanInfo) => {
            loanInfo.numberOfPropertiesOrUnits = loanInfo.numberOfUnits;
            loanInfo.submittedOn = loanInfo.submittedDate;
            loanInfo.amount = loanInfo.loanCommitment;
            delete loanInfo["numberOfUnits"];
            delete loanInfo["submittedDate"];
            delete loanInfo["loanCommitment"];
          });

          setLoanInfolist(loanInfolistData);
          setKanbanViews(sortLoansKanban(loanInfolistData));
        }
        else {
          await handleErrorResponse(response, setIsMessage, setErrorResponse);
        }
      }
      catch (error) {
        handleError();
      }

      setIsLoading(false);
    };

    if (isFirstLoad) {
      getGMRInfoAysnc();
    }
  }, [setIsMessage, setErrorResponse, setIsLoading, setLoanInfolist, handleError, proxyUser, isFirstLoad, filteredLoanData]);

  useEffect(() => {
    setIsFirstLoad(false);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (!isFetching) {
      return;
    }

    setCurrentSliceTo(currentSliceTo + 5);
    setIsFetching(false);
  }, [isFetching]);

  useEffect(() => {
    if (currentSliceTo !== GlobalConstants.DefaultValues.defaultSliceKanban) {
      setCurrentSliceTo(GlobalConstants.DefaultValues.defaultSliceKanban);
    }
  }, [filteredLoanData]);

  return (
    <div className={(isGreaterThanTablet && isSidebar) ? "landing-page hide-sidebar" : "landing-page sidebar"}>
      {isLoading ? <Loader className="" active inline="centered" size={"big"} /> : <div>
        <Card.Group>
          <BCMessage
            animation={"fly down"}
            duration={750}
            visible={isMessage}
            transitionOnMount
            styles="full-width"
            onDismiss={() => setIsMessage(false)}
            errorResponse={errorResponse}
          />
          <BCMenuBar 
            leftChildren ={[<React.Fragment>
            <div className="landing-page-menu-container">
              {isGreaterThanMobile ?
                <div className="landing-page-button-group">
                  {!getIsWhiteLabeled() && <BCButton brokerColor={brokerColor} labelPosition="left" iconName="add" handleOnClick={() => handleGMR()}>
                    <div>Start Pricing</div>
                  </BCButton>}
                  {!getIsWhiteLabeled() && <BCButton brokerColor={brokerColor} labelPosition="left" iconName="add" handleOnClick={() => handleApplication()}>
                    <div>Start Application</div>
                  </BCButton>}
                  {(loanInfolist && filteredLoanData?.length !== 0) && <Checkbox 
                    className="view-toggle" 
                    toggle 
                    label={{children: isKanbanView ? "Switch to table view!" : "Switch to card view!" }} 
                    onChange={() => handleInfolistViewType()} 
                    defaultChecked={!isKanbanView}
                  />}
                </div> : isValidPermission(permissions, "BrokerGMR", "read") && <BCButton brokerColor={brokerColor} handleOnClick={handleGMR}>
                  <Icon className="add" fitted />
                </BCButton>
              } 
            </div>
              </React.Fragment>     
            ]}
            
            rightChildren={[<BCFilterSearch reducerInstance={reducerInstance} hideFilterBar={!isValidPermission(permissions, "GlobalAdminAccess", "read")} />]}
            className={isValidPermission(permissions, "GlobalAdminAccess", "read") && isFilterOpen ? "no-bottom-margin" : ""}
          />
          {isValidPermission(permissions, "GlobalAdminAccess", "read") && <BCFilterBar reducerInstance={reducerInstance}/>}
        </Card.Group>
        {loanInfolist && filteredLoanData?.length === 0 ?
          <Card.Group>
           <Card fluid>
              <div className="landing-page-empty-container">
                <div className="landing-page-empty-content">
                  {!getIsWhiteLabeled() && <Image className="landing-page-empty-icon center" src={BCQuoteIcon} />}
                  <div className="landing-page-empty-text">{getIsWhiteLabeled() ? "It looks like there isn't any applications currently submitted for approval. When you do, you will be able to monitor your progress towards closing here." : "Looks like you don't have a loan ready for processing yet. Click below to get your loan priced in 5 minutes or less"}</div>
                  <div className="center">
                    {isValidPermission(permissions, "BrokerGMR", "read")  && <BCButton brokerColor={brokerColor} handleOnClick={() => handleGMR()}><div>Get Started</div></BCButton>}
                  </div>
                </div>
              </div>
            </Card>
          </Card.Group> : isKanbanView && isGreaterThanMobile ? <div className="broker-kanban-container">
            <BCKanban
              kanbanClass="processing-kanban"
              data={kanbanViews && kanbanViews[0] ? sortedLoansKanban : []}
              infoData={filteredLoanData}
              setData={loanInfolist}
              freezeLanes
              brokerColor={brokerColor}
              handleSummary={(id) => handleSummary(id)}
            />
          </div> : isKanbanView === true && isGreaterThanMobile === false ? <React.Fragment>
              <BCCards 
                isGreaterThanMobile={isGreaterThanMobile} 
                infoData={loanInfolist}
                displayList={filteredLoanData}
                handleGMR={false}
                handleSummary={(id) => handleSummary(id)}
                permissions={permissions}
                isKanban={false}
                brokerColor={brokerColor}
              />
            </React.Fragment> :
           isKanbanView === false ? <React.Fragment>
              <BCTable 
                data={convertToTableFormat(filterInfolist(loanInfolist))}
                brokerColor={brokerColor}
                handleClick={(index) => (loanInfolist[index] && loanInfolist[index].id) ? handleSummary(loanInfolist[index].id) : void 0}
                selectable
                sortable={["Name", "Loan Program", "Properties or Units", "Submitted On", "Status", "Stage", "Brokerage", "Broker Email"]}
                pagination={25}
              />
            </React.Fragment> : null
        }
      </div>}
    </div>
  );

};

export default BPLoans;
